import React, { useState } from 'react'
import { CheckIcon, ChevronDownIcon, LinkIcon, TagIcon, XIcon } from 'lucide-react'
import type { ConversationLabel } from '../../api/conversationLabels'
import type { ConversationAction, ConversationState } from '../../api/conversationActions'
import type { HandlingStatus, InboxAssignee } from '../../types/inbox'
import type { InboxEntityLinkField, InboxEntityLinkKey } from '../../lib/inboxEntityLinks'
import { AssigneePicker } from './AssigneePicker'
import { HandlingStatusSelect } from './HandlingStatusSelect'
import { InboxAssignmentHistoryPanel } from './InboxAssignmentHistoryPanel'
import { ConversationActionsMenu } from '../ConversationActionsMenu'

export interface OpenThreadOption {
  id: string
  label: string
  unread?: number
}

interface InboxConversationDetailHeaderProps {
  contactName: string
  contactInfo: string
  subject?: string | null
  channelLabel?: string
  conversationId: string
  handlingStatus: HandlingStatus
  waitingUntil: string | null
  assignedToUserId: string | null
  assignees: InboxAssignee[]
  canAssignOthers: boolean
  currentUserId: string | null
  entityLinkFields: InboxEntityLinkField[]
  linkedEntityValues: Partial<Record<InboxEntityLinkKey, string | null | undefined>>
  linkedLabels: Record<string, string>
  labels: ConversationLabel[]
  rowLabels: ConversationLabel[]
  conversationState: ConversationState
  stateBadge?: { label: string; className: string } | null
  isUnlinked: boolean
  /** Sibling open threads for the same contact — compact switcher in the header. */
  openThreads?: {
    currentId: string
    threads: OpenThreadOption[]
    onSelect: (id: string) => void
  } | null
  onAssign: (userId: string | null) => void
  onHandlingChange: (status: HandlingStatus, waitingUntil?: string | null) => void
  onResolve: () => void
  onLinkRecord: () => void
  onToggleLabelPicker: () => void
  onConversationAction: (action: ConversationAction) => void
  onNavigateEntity: (route: string) => void
  onUnlinkEntity: (key: string) => void
  onOpenUnlinkedModal: () => void
  labelPicker?: React.ReactNode
}

const OPEN_THREADS_VISIBLE = 3

function OpenThreadsSwitcher({
  currentId,
  threads,
  onSelect,
}: {
  currentId: string
  threads: OpenThreadOption[]
  onSelect: (id: string) => void
}) {
  const [open, setOpen] = useState(false)
  if (threads.length <= 1) return null

  const visibleThreads = threads.slice(0, OPEN_THREADS_VISIBLE)
  const overflowThreads = threads.slice(OPEN_THREADS_VISIBLE)

  function threadPill(t: OpenThreadOption) {
    const isCurrent = t.id === currentId
    return (
      <button
        key={t.id}
        type="button"
        disabled={isCurrent}
        title={t.label || 'Thread'}
        onClick={() => {
          if (isCurrent) return
          onSelect(t.id)
        }}
        className={`inline-flex items-center gap-1 max-w-[8.5rem] px-1.5 py-0.5 text-[11px] font-medium rounded-md border truncate ${
          isCurrent
            ? 'bg-blue-600 text-white border-blue-600 cursor-default'
            : 'text-blue-700 bg-blue-50 border-blue-200 hover:bg-blue-100'
        }`}
      >
        <span className="truncate">{t.label || 'Thread'}</span>
        {(t.unread || 0) > 0 && !isCurrent && (
          <span className="min-w-[1.1rem] h-4 px-1 rounded-full bg-blue-600 text-white text-[10px] font-semibold flex items-center justify-center flex-shrink-0">
            {t.unread! > 99 ? '99+' : t.unread}
          </span>
        )}
      </button>
    )
  }

  return (
    <div className="flex items-center gap-1 min-w-0 flex-wrap">
      <span className="text-[10px] font-medium text-gray-500 flex-shrink-0 whitespace-nowrap">Open threads</span>
      {visibleThreads.map(threadPill)}
      {overflowThreads.length > 0 && (
        <div className="relative flex-shrink-0">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[11px] font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100"
            title="More open threads"
          >
            +{overflowThreads.length} more
            <ChevronDownIcon className="w-3 h-3 text-blue-500" />
          </button>
          {open && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
              <div className="absolute left-0 mt-1 z-50 w-64 max-w-[min(16rem,calc(100vw-2rem))] bg-white border border-gray-200 rounded-lg shadow-lg py-1 max-h-56 overflow-y-auto">
                {overflowThreads.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    disabled={t.id === currentId}
                    className={`w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 flex items-center gap-2 ${
                      t.id === currentId ? 'bg-blue-50 text-blue-800 font-medium' : 'text-gray-800'
                    }`}
                    onClick={() => {
                      if (t.id === currentId) return
                      onSelect(t.id)
                      setOpen(false)
                    }}
                  >
                    <span className="truncate flex-1 min-w-0">{t.label || 'Thread'}</span>
                    {t.id === currentId && (
                      <span className="text-[10px] text-blue-600 flex-shrink-0">Current</span>
                    )}
                    {(t.unread || 0) > 0 && t.id !== currentId && (
                      <span className="min-w-[1.1rem] h-4 px-1 rounded-full bg-blue-600 text-white text-[10px] font-semibold flex items-center justify-center flex-shrink-0">
                        {t.unread! > 99 ? '99+' : t.unread}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}

export function InboxConversationDetailHeader({
  contactName,
  contactInfo,
  subject,
  conversationId,
  handlingStatus,
  waitingUntil,
  assignedToUserId,
  assignees,
  canAssignOthers,
  currentUserId,
  entityLinkFields,
  linkedEntityValues,
  linkedLabels,
  rowLabels,
  conversationState,
  stateBadge,
  isUnlinked,
  openThreads,
  onAssign,
  onHandlingChange,
  onResolve,
  onLinkRecord,
  onToggleLabelPicker,
  onConversationAction,
  onNavigateEntity,
  onUnlinkEntity,
  onOpenUnlinkedModal,
  labelPicker,
}: InboxConversationDetailHeaderProps) {
  const activeLinks = entityLinkFields.filter((f) => linkedEntityValues[f.key])

  return (
    <div className="flex-shrink-0 border-b border-gray-200 bg-white relative">
      <div className="px-3 sm:px-4 py-2 flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 min-w-0 flex-wrap">
            <h2 className="text-sm font-semibold text-gray-900 truncate">{contactName || contactInfo}</h2>
            {stateBadge && (
              <span className={`flex-shrink-0 px-2 py-0.5 text-[10px] font-medium rounded-full ${stateBadge.className}`}>
                {stateBadge.label}
              </span>
            )}
          </div>
          {openThreads && openThreads.threads.length > 1 && (
            <div className="mt-1">
              <OpenThreadsSwitcher
                currentId={openThreads.currentId}
                threads={openThreads.threads}
                onSelect={openThreads.onSelect}
              />
            </div>
          )}
          {subject && <p className="text-xs text-gray-600 truncate">{subject}</p>}
          {!subject && contactInfo && contactName !== contactInfo && (
            <p className="text-xs text-gray-500 truncate">{contactInfo}</p>
          )}
        </div>

        <div className="flex items-center gap-0.5 flex-shrink-0">
          <button
            type="button"
            onClick={onLinkRecord}
            title="Link to CRM record"
            className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700"
          >
            <LinkIcon className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={onToggleLabelPicker}
            title="Add or remove labels"
            className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700"
          >
            <TagIcon className="w-4 h-4" />
          </button>
          <AssigneePicker
            avatarOnly
            assignees={assignees}
            value={assignedToUserId}
            canAssignOthers={canAssignOthers}
            currentUserId={currentUserId}
            onChange={onAssign}
          />
          <button
            type="button"
            onClick={onResolve}
            title="Mark resolved"
            className={`p-1.5 rounded-lg hover:bg-gray-100 ${
              handlingStatus === 'resolved' ? 'text-green-600' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <CheckIcon className="w-4 h-4" />
          </button>
          <ConversationActionsMenu
            compact
            state={conversationState}
            onAction={onConversationAction}
            className="flex-shrink-0"
          />
        </div>
      </div>

      {(activeLinks.length > 0 || rowLabels.length > 0) && (
        <div className="px-3 sm:px-4 pb-1.5 flex items-center gap-1.5 flex-wrap">
          {activeLinks.map((f) => (
            <span
              key={f.key}
              className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 bg-gray-100 text-gray-700 text-[11px] rounded-full"
            >
              <button
                type="button"
                onClick={() => onNavigateEntity(f.route(linkedEntityValues[f.key]!))}
                className="hover:underline"
              >
                {f.label}: {linkedLabels[f.key] || '...'}
              </button>
              <button
                type="button"
                onClick={() => onUnlinkEntity(f.key)}
                className="text-gray-400 hover:text-red-600"
              >
                <XIcon className="w-2.5 h-2.5" />
              </button>
            </span>
          ))}
          {rowLabels.map((label) => (
            <span
              key={label.id}
              className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-gray-100 text-gray-700 text-[10px] rounded-full"
            >
              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: label.color }} />
              <span className="truncate max-w-[6rem]">{label.name}</span>
            </span>
          ))}
        </div>
      )}

      <div className="px-3 sm:px-4 pb-1.5 flex items-center gap-2 flex-wrap">
        <HandlingStatusSelect
          value={handlingStatus}
          waitingUntil={waitingUntil}
          onChange={onHandlingChange}
          compact
          hideBadge
        />
        <InboxAssignmentHistoryPanel itemType="conversation" itemId={conversationId} assignees={assignees} />
      </div>

      {isUnlinked && (
        <div className="flex items-center justify-between gap-2 border-t border-amber-100 bg-amber-50/60 px-3 sm:px-4 py-1">
          <span className="text-[11px] text-amber-800">No customer linked to this conversation</span>
          <button
            type="button"
            onClick={onOpenUnlinkedModal}
            className="text-[11px] font-medium text-amber-900 hover:underline flex-shrink-0"
          >
            Link customer
          </button>
        </div>
      )}

      {labelPicker}
    </div>
  )
}
